import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ifcRootGuid } from '@constants'
import { getFileUrl } from '@domainUtils'
import { SceneLoadingIndicatorFallback } from '@scene'
import { Color, Group, Object3DEventMap } from 'three'
import { useTheme } from '@mui/material'
import { GlbFileAdditionalData } from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'
import { getGuidToMGroup } from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import { ThreeEvent } from '@react-three/fiber'
import { coordinateEventName, publishCoordinateEvent } from '@ui/events'
import { useTriggerContextMenu } from '@ui/hooks'
import { useAppStore, useIfcStore } from '@stores'
import { useControlStore, useSceneStore } from '@editorStores'
import {
  useProjectFiles,
  useIfcModelMetadata,
  useMouseMovedSinceMouseDown,
  useIfcImportSelectionActive,
  useIfcGuidToHidden,
  useIfcSelection,
} from '@editorHooks'
import useEmitModelClickListenerV2 from '../ModelMeshV2/hooks/useEmitModelClickListenerV2'
import GlbModelViewer from './components/GLTFModelViewer'

const IFCGlbModel: React.FC = () => {
  const { projectId } = useParams()
  const theme = useTheme()

  const addLoadedGltfModel = useSceneStore(state => state.addLoadedGltfModel)
  const removeLoadedGltfModel = useSceneStore(state => state.removeLoadedGltfModel)

  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const showGltfModel = useControlStore(state => state.showGltfModel)
  const gltfModelTranslucent = useControlStore(state => state.gltfModelTranslucent)
  const applyIfcAlignmentConfig = useControlStore(state => state.applyIfcAlignmentConfig)
  const selectionModeKey = useControlStore(state => state.selectionModeKey)
  const setCoordinateExplorerEnabled = useControlStore(state => state.setCoordinateExplorerEnabled)
  const { selectedIfcElementGuids } = useIfcSelection()
  const ifcDrawer = useIfcImportSelectionActive()
  const isMarqueeSelection = useControlStore(state => state.isMarqueeSelection)
  const ifcSelectionDisabled = useIfcStore(state =>
    Array.from(state.ifcSelectionDisabledById.values()).some(Boolean),
  )
  const ifcColorOverrideByGuid = useIfcStore(state => state.ifcColorOverrideByGuid)
  const ifcColorOverrideDefault = useIfcStore(state => state.ifcColorOverrideDefault)
  const highlightedIfcElementGuids = useControlStore(state => state.highlightedIfcElementGuids)

  const ifcTransparentOpacity = useAppStore(state => state.ifcTransparentOpacity)

  const { data: filesData } = useProjectFiles(ifcRootGuid)

  const uploadedGlbFile = useMemo(() => {
    if (filesData?.files) {
      const glbFiles = filesData.files.filter(
        (file: FileItem) =>
          file.mimetype === 'application/glb' ||
          file.mimetype === 'model/gltf-binary' ||
          file.mimetype === 'application/octet-stream',
      )
      if (glbFiles.length > 0) {
        const activeFile =
          activeIfcFileGuid != null
            ? glbFiles.find((f: FileItem) => f.guid === activeIfcFileGuid)
            : null
        return activeFile ?? glbFiles[glbFiles.length - 1]
      }
    }

    return null
  }, [filesData, activeIfcFileGuid])

  const alignmentConfig = (uploadedGlbFile?.additional_data as GlbFileAdditionalData | undefined)
    ?.alignment_config

  const metadata = useIfcModelMetadata(uploadedGlbFile?.guid ?? null)
  const guidToMGroup = useMemo(() => getGuidToMGroup(metadata ?? null), [metadata])
  const guidToColor = useMemo(() => {
    const ifcPalette = theme.scenePalette.ifc
    const typeToColor = {
      inner_walls: ifcPalette.inner_walls,
      outer_walls: ifcPalette.outer_walls,
      slabs: ifcPalette.slabs,
      roof_slabs: ifcPalette.roof_slabs,
      roofs: ifcPalette.roofs,
      columns: ifcPalette.columns,
      beams: ifcPalette.beams,
      openings: ifcPalette.openings,
      doors: ifcPalette.doors,
      windows: ifcPalette.windows,
      members: ifcPalette.members,
      stairs: ifcPalette.stairs,
      furniture: ifcPalette.furniture,
      unclassified: ifcPalette.unclassified,
    } as const

    return Object.entries(guidToMGroup).reduce((acc, [guid, type]) => {
      // A per-guid override (e.g. storey coloring) wins over the default type-based color; while an
      // override is active, unmapped guids fall back to its default color (e.g. grey for elements
      // without a storey assignment) rather than the type color.
      const color =
        ifcColorOverrideByGuid[guid] ??
        ifcColorOverrideDefault ??
        typeToColor[type as keyof typeof typeToColor]
      if (color) {
        acc[guid] = new Color(color as string)
      }
      if (highlightedIfcElementGuids.includes(guid)) {
        acc[guid] = new Color(theme.scenePalette.selection)
      }
      return acc
    }, {} as Record<string, Color>)
  }, [
    theme.scenePalette.ifc,
    theme.scenePalette.selection,
    guidToMGroup,
    highlightedIfcElementGuids,
    ifcColorOverrideByGuid,
    ifcColorOverrideDefault,
  ])
  const guidToHidden = useIfcGuidToHidden(metadata ?? null)

  const executeListener = useEmitModelClickListenerV2()

  const { openContextMenu } = useTriggerContextMenu()
  const { mouseMoved } = useMouseMovedSinceMouseDown()

  const handleClick = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => {
      if (selectionModeKey?.startsWith(coordinateEventName)) {
        publishCoordinateEvent({
          x: event.point.x,
          y: event.point.y,
          z: event.point.z,
        })
        setCoordinateExplorerEnabled(false)
        event.stopPropagation()
        return
      }

      executeListener(guid, event, { type: 'ifc_glb_model' })
    },
    [executeListener, selectionModeKey, setCoordinateExplorerEnabled],
  )

  const handleContextMenu = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => {
      if (mouseMoved.current) return

      openContextMenu(
        event.point,
        guid,
        {
          hide: {
            guid,
          },
          filter: {
            guid,
          },
        },
        {},
      )
    },
    [mouseMoved, openContextMenu],
  )

  // Load GLB model if we have a file
  const glbUrl = useMemo(() => {
    if (uploadedGlbFile && projectId) {
      return getFileUrl(uploadedGlbFile, projectId, ifcRootGuid)
    }
    return null
  }, [uploadedGlbFile, projectId])

  const [modelGuid, setModelGuid] = useState<string | null>(null)

  const onLoad = (gltfModel: Group<Object3DEventMap>) => {
    if (!uploadedGlbFile?.guid) return

    addLoadedGltfModel(uploadedGlbFile?.guid, gltfModel)
    setModelGuid(uploadedGlbFile?.guid ?? null)
  }

  useEffect(() => {
    return () => {
      if (modelGuid) {
        removeLoadedGltfModel(modelGuid)
        setModelGuid(null)
      }
    }
  }, [modelGuid, removeLoadedGltfModel])

  const isActiveFile = uploadedGlbFile?.guid === activeIfcFileGuid
  const useAlignment = isActiveFile && applyIfcAlignmentConfig && alignmentConfig != null
  // Apply t1 first (translation), then R (Z rotation): world = R * (local + t1).
  // t1 = referencePointModelCS - referencePointIFCCS
  const alignmentTransform = useMemo(() => {
    if (!useAlignment || !alignmentConfig) return null
    const PIFCRef = alignmentConfig.referencePointIFCCS ?? [0, 0, 0]
    const PModelRef = alignmentConfig.referencePointModelCS ?? [0, 0, 0]
    const deg = alignmentConfig.zRotationDegrees ?? 0
    const rad = (deg * Math.PI) / 180
    const half = rad / 2
    const R: [number, number, number, number] = [0, 0, Math.sin(half), Math.cos(half)]
    return { PIFCRef, PModelRef, R }
  }, [useAlignment, alignmentConfig])

  const gltfOpacity = gltfModelTranslucent ? ifcTransparentOpacity : 1

  if (!glbUrl) {
    return null
  }

  return (
    <Suspense fallback={<SceneLoadingIndicatorFallback description="Gltf Model wird geladen..." />}>
      <group visible={showGltfModel}>
        <GlbModelViewer
          key={activeIfcFileGuid}
          glbUrl={glbUrl}
          guidToHidden={guidToHidden}
          guidToColor={guidToColor}
          selectedIfcElementGuids={selectedIfcElementGuids}
          onElementClick={showGltfModel && !ifcSelectionDisabled ? handleClick : undefined}
          onContextMenuElement={
            showGltfModel && !ifcSelectionDisabled ? handleContextMenu : undefined
          }
          onLoad={onLoad}
          alignmentTransform={alignmentTransform}
          opacity={gltfOpacity}
          enableHover={ifcDrawer && !isMarqueeSelection && showGltfModel && !ifcSelectionDisabled}
        />
      </group>
    </Suspense>
  )
}

export default IFCGlbModel
